import type { ObjectId } from "@bbj-types/utils";
import { CheckboxStatus } from "@root/utils/enums/checkbox";
import { type MouseEvent, useCallback, useMemo } from "react";
import type { IAutocompleteAppellationsProps } from "../index";
import type { TAutocompleteAppellationsOption } from "./options";

export interface IAutocompleteAppellationsOptionsGroups {
  [rome: string]: {
    label: string;
    options: TAutocompleteAppellationsOption[];
    optionsSet: Set<ObjectId>;
  };
}

export const useAutocompleteAppellationsGroups = (
  value: IAutocompleteAppellationsProps["value"],
  valueSet: Set<ObjectId>,
  onChange: IAutocompleteAppellationsProps["onChange"],
  options: TAutocompleteAppellationsOption[]
) => {
  const optionsGroups = useMemo(() => {
    const optionsGroups: IAutocompleteAppellationsOptionsGroups = {};
    for (const option of options) {
      if (optionsGroups[option.rome] === undefined) {
        optionsGroups[option.rome] = {
          label: option.romeLabel,
          options: [option],
          optionsSet: new Set<ObjectId>([option._id]),
        };
      } else {
        optionsGroups[option.rome].options.push(option);
        optionsGroups[option.rome].optionsSet.add(option._id);
      }
    }
    return optionsGroups;
  }, [options]);

  const getOptionGroupLabel = useCallback(
    (group: string) => {
      return optionsGroups[group].label;
    },
    [optionsGroups]
  );

  const getOptionGroupStatus = useCallback(
    (group: string) => {
      let status = CheckboxStatus.NONE;
      for (const option of optionsGroups[group].options) {
        if (valueSet.has(option._id)) {
          status |= CheckboxStatus.CHECKED;
        } else {
          status |= CheckboxStatus.UNCHECKED;
        }
      }
      return status;
    },
    [valueSet, optionsGroups]
  );

  const handleClickOptionGroup = useCallback(
    (event: MouseEvent, group: string, groupStatus: CheckboxStatus) => {
      event.preventDefault();
      if (groupStatus === CheckboxStatus.CHECKED) {
        const newValue = value.filter((v) => !optionsGroups[group].optionsSet.has(v._id));
        onChange(event, newValue, "removeOption");
      } else {
        const newValue = [...value];
        for (const option of optionsGroups[group].options) {
          if (!valueSet.has(option._id)) {
            newValue.push(option);
          }
        }
        onChange(event, newValue, "selectOption");
      }
    },
    [value, valueSet, onChange, optionsGroups]
  );

  return {
    groupsLength: Object.keys(optionsGroups).length,
    getOptionGroupLabel,
    getOptionGroupStatus,
    handleClickOptionGroup,
  } as const;
};
